/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, ReactElement } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import SearchUtils from '../../../../utils/SearchUtils';
import LinkOutIcon from '@react/react-spectrum/Icon/LinkOut';
import { Utils } from '../../../../utils/Globals';
import './SearchDropdownSparkCard.scss';
import Button from '@react/react-spectrum/Button';
import Add from '@react/react-spectrum/Icon/Add';

type Props = {
	resultItem: ResultItem;
	type?: string;
	isListItem?: boolean;
	onClickSourceTitle?: (...args: Array<any>) => any;
	showName?: boolean;
	onTempalteClick: () => any;
	isDarkMode?: boolean;
	index?: number;
	lastCard?: boolean;
} & WrappedComponentProps;
type State = {
	rendition: string;
	listItemHoverState: boolean;
};

class SearchDropdownSparkCard extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			rendition: '',
			listItemHoverState: false,
		};
	}

	/**
	 * [Updates the hover state to drive the display of the link-out icon.
	 * The hover state is updated by the item title and image elements.]
	 * @param {[boolean]} hoverState
	 */
	setListItemHoverState(hoverState: boolean) {
		this.setState({
			listItemHoverState: hoverState,
		});
	}

	render() {
		let jsx: ReactElement | null = null;
		const { intl } = this.props;
		if (this.props.resultItem && this.props.resultItem.attributes) {
			const backgroundImageUrl = this.props.resultItem.attributes.imageRenditionUrl || '';
			const sparkText = SearchUtils.sparkTypeToText({
				sparkType: this.props.resultItem.attributes.subType || '',
				template: true,
			});
			const data = this.props.resultItem.attributes.data;
			const attributes = this.props.resultItem.attributes;
			const rendition = attributes.rendition;
			const calculatedDimension = attributes.dimensionName === 'width' ? 'height' : 'width';
			let calculatedValue;
			if (attributes.dimensionName === 'height') {
				calculatedValue = +attributes.dimensionSize / (rendition.maxHeight / rendition.maxWidth);
			} else {
				calculatedValue = +attributes.dimensionSize * (rendition.maxHeight / rendition.maxWidth);
			}
			let useCaseArray: any[] = [];

			if (data && data.type === 'spark') {
				useCaseArray = data.value.useCases;
			}

			let useCases = SearchUtils.getUseCaseForSparkTemplates(useCaseArray);
			if (useCases.length > 20) {
				useCases = useCases.substring(0, 19).concat('...');
			}
			if (this.props.isListItem) {
				const translationId = SearchUtils.getTranslationIdForSparkRebranding(
					'app.search.ccexpress_templates.title',
				);
				const isMobile = Utils.isMobileOrTabletBrowser();
				jsx = (
					<div
						className='top-results-list-item-container'
						tabIndex={0}
						onKeyDown={(event) => SearchUtils.onKeyDown(event)}
						onFocus={() => this.setListItemHoverState(true)}
						onBlur={() => this.setListItemHoverState(false)}
					>
						<div
							className='top-results-list-item-image-container-tutorial'
							onMouseEnter={() => this.setListItemHoverState(true)}
							onMouseLeave={() => this.setListItemHoverState(false)}
						>
							<div
								className='image-container-content-tutorial'
								style={{
									backgroundImage: `url(${backgroundImageUrl})`,
									[attributes.dimensionName]: `${attributes.dimensionSize}px`,
									[calculatedDimension]: `${calculatedValue}px`,
								}}
							/>
						</div>
						{this.props.showName && (
							<div className='top-results-list-item-text-container'>
								<div
									className='text-container-type-text'
									onClick={this.props.onClickSourceTitle}
									tabIndex={0}
									onKeyDown={(event) => SearchUtils.onKeyDown(event, true)}
									role='link'
								>
									<FormattedMessage id={translationId} />
								</div>
								<div
									className='text-container-title-text'
									style={
										!isMobile && this.state.listItemHoverState
											? {
													textDecoration: 'underline',
												}
											: {}
									}
								>
									<span
										onMouseEnter={() => this.setListItemHoverState(true)}
										onMouseLeave={() => this.setListItemHoverState(false)}
									>
										{useCases}
									</span>
									<LinkOutIcon
										className={
											!isMobile && this.state.listItemHoverState
												? 'text-container-title-icon'
												: 'text-container-title-icon-hidden'
										}
										label={null}
										size='S'
									/>
								</div>
								<div className='text-container-description-text'>
									{this.props.intl.formatMessage({
										id: sparkText,
									})}
								</div>
								<div className='text-container-extra-info-text' />
							</div>
						)}
					</div>
				);
			} else {
				jsx = (
					<div
						className={`search-card-3 search-dropdown-spark-card${this.props.isDarkMode ? ' dark-mode' : ''}
						${this.props.index != null ? ` item-${this.props.index}` : ''}
						${this.props.lastCard ? ' last-item' : ''}`}
						onKeyDown={(event) => SearchUtils.onKeyDown(event)}
					>
						<div className='search-card-image-container'>
							<div
								className='search-card-image-fit-no-icon'
								tabIndex={0}
								style={{
									backgroundImage: `url(${backgroundImageUrl})`,
									[attributes.dimensionName]: `${attributes.dimensionSize}px`,
									[calculatedDimension]: `${calculatedValue}px`,
								}}
							>
								<div
									style={{
										[attributes.dimensionName]: `${attributes.dimensionSize}px`,
										[calculatedDimension]: `${calculatedValue}px`,
									}}
									onClick={this.props.onTempalteClick}
									className='search-dropdown-spark-card-hoverstate'
								/>
								<Button
									className='search-spark-card-hover-button'
									variant='tool'
									icon={<Add />}
									onClick={this.props.onTempalteClick}
								/>
							</div>
						</div>
						{this.props.showName && (
							<div className='search-card-3-text-container search-dropdown-spark-card-title-container'>
								<div className='search-card-3-cap-text-title search-dropdown-spark-card-title'>
									{useCases}
								</div>
							</div>
						)}
					</div>
				);
			}
		}

		return jsx;
	}
}

export default injectIntl(SearchDropdownSparkCard);
